import { useEffect, useState } from "react";
import { LogoMark } from "@/components/Logo";

// Keep the branded intro to a brief flash. It no longer waits on `window.load`
// (which stalls on images/iframes) so it never gates LCP behind the full page.
const MIN_MS = 150;
const FADE_MS = 400;

interface Props {
  onDone?: () => void;
}

const PremiumLoader = ({ onDone }: Props) => {
  const [hiding, setHiding] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    let removeTimer: ReturnType<typeof setTimeout>;
    const hideTimer = setTimeout(() => {
      setHiding(true);
      removeTimer = setTimeout(() => {
        setRemoved(true);
        onDone?.();
      }, FADE_MS);
    }, MIN_MS);

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [onDone]);

  if (removed) return null;

  return (
    <div
      aria-hidden={hiding}
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[10000] flex items-center justify-center transition-opacity duration-[400ms] ${
        hiding ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{
        background:
          "radial-gradient(ellipse at center, hsl(var(--accent)) 0%, hsl(var(--accent-dark)) 78%)",
      }}
    >
      {/* Subtle grid backdrop */}
      <div aria-hidden="true" className="absolute inset-0 hero-grid-overlay opacity-30" />

      <div className="relative flex flex-col items-center px-6">
        {/* Emblem with animated rings */}
        <div className="relative flex items-center justify-center mb-7">
          <div
            aria-hidden="true"
            className="absolute -inset-10 rounded-full opacity-70 blur-3xl motion-safe:animate-pulse"
            style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.5) 0%, transparent 70%)" }}
          />
          {/* Outer rotating ring */}
          <div
            aria-hidden="true"
            className="absolute -inset-5 rounded-full border-2 border-transparent motion-safe:animate-spin"
            style={{
              borderTopColor: "hsl(var(--secondary))",
              borderRightColor: "hsl(var(--secondary) / 0.25)",
              animationDuration: "2.2s",
            }}
          />
          {/* Inner counter-rotating ring */}
          <div
            aria-hidden="true"
            className="absolute -inset-2 rounded-full border-2 border-transparent motion-safe:animate-spin"
            style={{
              borderBottomColor: "hsl(var(--primary))",
              borderLeftColor: "hsl(var(--primary) / 0.3)",
              animationDuration: "1.4s",
              animationDirection: "reverse",
            }}
          />
          <div className="relative motion-safe:animate-[loader-emblem_2.4s_ease-in-out_infinite]">
            <LogoMark className="h-20 w-20 drop-shadow-[0_8px_30px_rgba(0,0,0,0.45)]" />
          </div>
        </div>

        {/* Wordmark */}
        <div className="text-center mb-6">
          <div className="font-heading font-extrabold text-white text-xl tracking-tight leading-none">
            Summit Neighbor
          </div>
          <div className="font-heading font-semibold uppercase tracking-[0.3em] text-[0.62rem] text-secondary mt-2">
            Fencing Co.
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-[3px] w-48 overflow-hidden rounded-full bg-white/10">
          <div
            className="absolute inset-y-0 left-0 w-1/3 rounded-full"
            style={{
              background: "linear-gradient(90deg, transparent, hsl(var(--secondary)), transparent)",
              animation: "premium-loader-bar 1.2s ease-in-out infinite",
            }}
          />
        </div>
        <p className="relative mt-4 text-[0.65rem] tracking-[0.28em] uppercase text-white/55">
          Building your experience
        </p>
      </div>

      <style>{`
        @keyframes premium-loader-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes loader-emblem {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="status"] * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;
